type VariantSpec = {
  dimension: string;
  value: string;
};

type VariantLike = {
  variant_specifics?: VariantSpec[] | null;
};

const VARIANT_DIMENSION_ALIASES: Record<string, string[]> = {
  "Service Provider": ["Service Provider", "Service provider", "Provider"],
  "Product Grade": ["Product Grade", "Product grade", "Grade"],
  Configuration: ["Configuration"],
  Size: ["Size"],
  Style: ["Style"],
  Color: ["Color"],
  Capacity: ["Capacity"],
  Set: ["Set"],
  "Flavor Name": ["Flavor Name", "Scent"],
  CPU: ["CPU"],
  "Pattern Name": ["Pattern Name"],
};

const normalizeDimension = (value: string) => value.trim().toLowerCase();

const getVariantDimensionAliases = (dimension: string): string[] => {
  const normalized = normalizeDimension(dimension);
  const matchedEntry = Object.entries(VARIANT_DIMENSION_ALIASES).find(
    ([canonicalDimension, aliases]) =>
      normalizeDimension(canonicalDimension) === normalized ||
      aliases.some((alias) => normalizeDimension(alias) === normalized),
  );

  if (!matchedEntry) {
    return [dimension];
  }

  const [canonicalDimension, aliases] = matchedEntry;
  return Array.from(new Set([canonicalDimension, ...aliases]));
};

export const getVariantSpecValue = (
  variant: VariantLike | null | undefined,
  dimension: string,
): string | undefined => {
  if (!variant?.variant_specifics?.length) {
    return undefined;
  }

  const normalizedAliases = new Set(
    getVariantDimensionAliases(dimension).map(normalizeDimension),
  );

  return variant.variant_specifics.find((spec) =>
    normalizedAliases.has(normalizeDimension(spec.dimension)),
  )?.value;
};

export const getExactVariantSpecValue = (
  variant: VariantLike | null | undefined,
  dimension: string,
): string | undefined => {
  if (!variant?.variant_specifics?.length) {
    return undefined;
  }

  const normalizedDimension = normalizeDimension(dimension);
  return variant.variant_specifics.find(
    (spec) => normalizeDimension(spec.dimension) === normalizedDimension,
  )?.value;
};

export const hasExactVariantDimension = (
  variants: VariantLike[] | null | undefined,
  dimension: string,
): boolean => {
  if (!variants?.length) {
    return false;
  }

  const normalizedDimension = normalizeDimension(dimension);
  return variants.some((variant) =>
    variant.variant_specifics?.some(
      (spec) => normalizeDimension(spec.dimension) === normalizedDimension,
    ),
  );
};
